// ==================== TEXTAREA COMPONENT ====================

import { forwardRef, useId } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '../../utils/helpers';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  success?: string;
  hint?: string;
  showCharCount?: boolean;
  maxLength?: number;
  fullWidth?: boolean;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      success,
      hint,
      showCharCount = false,
      maxLength,
      fullWidth = true,
      resize = 'vertical',
      className,
      disabled,
      id,
      value,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const textareaId = id || generatedId;
    const charCount = typeof value === 'string' ? value.length : 0;

    const resizeStyles = {
      none: 'resize-none',
      vertical: 'resize-y',
      horizontal: 'resize-x',
      both: 'resize',
    };

    const baseStyles = `
      w-full px-4 py-3 rounded-md min-h-[120px]
      bg-(--color-charcoal-black) 
      border border-(--color-border-gray)
      text-white placeholder:text-(--color-muted-gray)
      focus:outline-none focus:ring-2 focus:ring-(--color-signal-orange)/50 focus:border-(--color-signal-orange)
      transition-all duration-200
      disabled:opacity-50 disabled:cursor-not-allowed
    `;

    const errorStyles = error ? 'border-(--color-error) focus:ring-(--color-error)/50 focus:border-(--color-error)' : '';
    const successStyles = success ? 'border-(--color-success) focus:ring-(--color-success)/50 focus:border-(--color-success)' : '';

    return (
      <div className={cn('relative', fullWidth && 'w-full')}>
        {/* Label */}
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium text-(--color-soft-gray) mb-2"
          >
            {label}
            {props.required && <span className="text-(--color-error) ml-1">*</span>}
          </label>
        )}

        {/* Textarea */}
        <textarea
          ref={ref}
          id={textareaId}
          disabled={disabled}
          maxLength={maxLength}
          value={value}
          className={cn(
            baseStyles,
            errorStyles,
            successStyles,
            resizeStyles[resize],
            className
          )}
          {...props}
        />

        {/* Bottom Row: Messages & Character Count */}
        <div className="flex justify-between items-start mt-2">
          <div className="flex-1">
            {/* Error Message */}
            {error && (
              <p className="text-sm text-(--color-error) flex items-center gap-1">
                <AlertCircle size={14} />
                {error}
              </p>
            )}

            {/* Success Message */}
            {success && !error && (
              <p className="text-sm text-(--color-success) flex items-center gap-1">
                <CheckCircle2 size={14} />
                {success}
              </p>
            )}

            {/* Hint */}
            {hint && !error && !success && (
              <p className="text-sm text-(--color-muted-gray)">{hint}</p>
            )}
          </div>

          {/* Character Count */}
          {showCharCount && maxLength && (
            <span
              className={cn(
                'text-sm ml-4',
                charCount >= maxLength
                  ? 'text-(--color-error)'
                  : charCount >= maxLength * 0.9
                  ? 'text-(--color-warning)'
                  : 'text-(--color-muted-gray)'
              )}
            >
              {charCount}/{maxLength}
            </span>
          )}
        </div>
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };

